import {Component, Inject, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatSelectModule} from '@angular/material/select';
import {MatTabsModule} from '@angular/material/tabs';
import {MatCardModule} from '@angular/material/card';
import {MatChipsModule} from '@angular/material/chips';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {CommonModule} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {EqualsPipe, GetPropertyPipe} from '../../pipes';

interface Constraint {
  id?: string;
  scheduleId: string | number;
  definition: string;
  humanReadableDefinition: string;
  type: 'hard' | 'soft';
  weight?: number;
  active?: boolean;
}

interface LibraryConstraint {
  id: number;
  slug: string;
  name: string;
  description: string;
  category: string;
  type: 'hard' | 'soft';
  defaultWeight: number;
  active: boolean;
}

interface LibraryConstraintsByCategory {
  [category: string]: LibraryConstraint[];
}

interface LibraryStatus {
  [slug: string]: {
    added: boolean;
    hasUpdate: boolean;
    constraintId?: number;
  };
}

@Component({
  selector: 'app-constraint-dialog',
  templateUrl: './constraint-dialog.component.html',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    FormsModule,
    MatDialogModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatTabsModule,
    MatCardModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    CommonModule,
    GetPropertyPipe,
    EqualsPipe,
  ],
})
export class ConstraintDialogComponent implements OnInit {
  isEditMode = false;
  selectedTabIndex = 0;

  libraryConstraints: LibraryConstraint[] = [];
  libraryByCategory: LibraryConstraintsByCategory = {};
  categories: string[] = [];
  loadingLibrary = false;
  selectedLibraryConstraint: LibraryConstraint | null = null;
  libraryWeight: number = 100;
  libraryStatus: LibraryStatus = {};

  private apiBaseUrl = environment.apiBaseUrl;

  constructor(
    public dialogRef: MatDialogRef<ConstraintDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Partial<Constraint>,
    private http: HttpClient
  ) {
    this.isEditMode = !!this.data.id;

    if (!this.data.type) {
      this.data.type = 'hard';
    }
    if (this.data.type === 'soft' && !this.data.weight) {
      this.data.weight = 100;
    }
  }

  ngOnInit() {
    if (!this.isEditMode) {
      this.loadLibraryConstraints();
    }
  }

  loadLibraryConstraints() {
    this.loadingLibrary = true;

    // Load both library constraints and status in parallel
    this.http.get<LibraryConstraint[]>(`${this.apiBaseUrl}/constraint-library`)
      .subscribe({
        next: (constraints) => {
          this.libraryConstraints = constraints;
          this.libraryByCategory = {};
          constraints.forEach(c => {
            if (!this.libraryByCategory[c.category]) {
              this.libraryByCategory[c.category] = [];
            }
            this.libraryByCategory[c.category].push(c);
          });
          this.categories = Object.keys(this.libraryByCategory).sort();

          // Load library status for this schedule
          if (this.data.scheduleId) {
            this.loadLibraryStatus();
          } else {
            this.loadingLibrary = false;
          }
        },
        error: (err) => {
          console.error('Failed to load library constraints:', err);
          this.loadingLibrary = false;
        }
      });
  }

  loadLibraryStatus() {
    this.http.get<LibraryStatus>(`${this.apiBaseUrl}/schedules/${this.data.scheduleId}/library-status`)
      .subscribe({
        next: (status) => {
          this.libraryStatus = status;
          this.loadingLibrary = false;
        },
        error: (err) => {
          console.error('Failed to load library status:', err);
          this.loadingLibrary = false;
        }
      });
  }

  isAdded(constraint: LibraryConstraint): boolean {
    return this.libraryStatus[constraint.slug]?.added ?? false;
  }

  hasUpdate(constraint: LibraryConstraint): boolean {
    return this.libraryStatus[constraint.slug]?.hasUpdate ?? false;
  }

  selectLibraryConstraint(constraint: LibraryConstraint) {
    this.selectedLibraryConstraint = constraint;
    this.libraryWeight = constraint.defaultWeight;
  }

  onSubmit() {
    if (this.data.humanReadableDefinition && this.data.type) {
      this.dialogRef.close(this.data);
    }
  }

  onAddFromLibrary() {
    if (this.selectedLibraryConstraint) {
      this.dialogRef.close({
        fromLibrary: true,
        librarySlug: this.selectedLibraryConstraint.slug,
        weight: this.selectedLibraryConstraint.type === 'soft' ? this.libraryWeight : undefined,
        active: true
      });
    }
  }

  onUpdateFromLibrary() {
    if (this.selectedLibraryConstraint) {
      this.dialogRef.close({
        fromLibrary: true,
        updateLibrary: true,
        librarySlug: this.selectedLibraryConstraint.slug,
      });
    }
  }

  getButtonLabel(): string {
    if (!this.selectedLibraryConstraint) {
      return 'Select a constraint';
    }
    if (this.isAdded(this.selectedLibraryConstraint)) {
      if (this.hasUpdate(this.selectedLibraryConstraint)) {
        return 'Update from Library';
      }
      return 'Already Added';
    }
    return 'Add from Library';
  }

  isButtonDisabled(): boolean {
    if (!this.selectedLibraryConstraint) {
      return true;
    }
    if (this.isAdded(this.selectedLibraryConstraint) && !this.hasUpdate(this.selectedLibraryConstraint)) {
      return true;
    }
    return false;
  }

  onLibraryAction() {
    if (!this.selectedLibraryConstraint) return;

    if (this.isAdded(this.selectedLibraryConstraint) && this.hasUpdate(this.selectedLibraryConstraint)) {
      this.onUpdateFromLibrary();
    } else if (!this.isAdded(this.selectedLibraryConstraint)) {
      this.onAddFromLibrary();
    }
  }

  getCategoryLabel(category: string): string {
    return category.charAt(0).toUpperCase() + category.slice(1);
  }
}
